import { useEffect, useState } from "react"
import { addComplaint, downloadComplaintFile, updateComplaint } from "../../services/AppService";
import { getAllDepartments } from "../../services/AdminServices";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import { IoClose, IoTrashOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { FaEye } from "react-icons/fa";
import { resolveMediaUrl } from "../../utility/media";

const ComplaintFormModal = ({ isOpen, mode, editData, onClose, onRefresh, onSubmit }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        complainerName: "",
        complainAgainst: "",
        complaintDetails: "",
        complaintDate: "",
        departmentId: ""
    });

    const [attachmentRows, setAttachmentRows] = useState([
        { id: Date.now(), description: "", file: null, fileName: "" }
    ]);

    const [departments, setDepartments] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    useEffect(() => {
        if (isOpen) {
            loadDepartments();
            if (editData) {
                setFormData({
                    complainerName: editData.complainerName || "",
                    complainAgainst: editData.complainAgainst || "",
                    complaintDetails: editData.complaintDetails || "",
                    complaintDate: editData.complaintDate || "",
                    departmentId: editData.departmentId || ""
                });
                // Map existing attachments if available
                if (editData.attachments) {
                    setAttachmentRows(editData.attachments.map(att => ({
                        id: att.id,
                        description: att.description || "",
                        file: null,
                        fileName: att.fileName || "",
                        fileUrl: att.fileUrl || ""
                    })));
                }
            } else {
                resetForm();
            }
        }
    }, [isOpen, editData]);

    const loadDepartments = async () => {
        try {
            const data = await getAllDepartments();
            setDepartments(data.data || []);
        } catch (error) {
            console.error("Failed to load departments", error);
        }
    }

    const resetForm = () => {
        setFormData({ complainerName: "", complainAgainst: "", complaintDetails: "", complaintDate: "", departmentId: "" });
        setAttachmentRows([{ id: Date.now(), description: "", file: null, fileName: "" }]);
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Attachment Table Logic
    const addRow = () => setAttachmentRows([...attachmentRows, { id: Date.now(), description: "", file: null, fileName: "" }]);

    const removeRow = (id) => {
        if (attachmentRows.length > 1) setAttachmentRows(attachmentRows.filter(row => row.id !== id));
    };

    const handleFileChange = (id, e) => {
        const file = e.target.files[0];
        if (file) {
            setAttachmentRows(prev => prev.map(row =>
                row.id === id ? { ...row, file: file, fileName: file.name } : row
            ));
        }
    };

    const handleInternalSubmit = (e) => {
        e.preventDefault();
        setConfirmOpen(true);
    };

    const handleConfirmFinal = () => {
        // Prepare Multi-part form data similar to Plan Estimates
        const payload = {
            ...formData,
            departmentId: Number(formData.departmentId),
            attachments: attachmentRows.map(row => ({
                id: row.id > 1000000000000 ? null : row.id,
                description: row.description.trim()
            }))
        };

        const finalData = new FormData();
        finalData.append("complaint", new Blob([JSON.stringify(payload)], { type: "application/json" }));

        attachmentRows.forEach(row => {
            if (row.file) finalData.append("files", row.file);
        });

        onSubmit(finalData);
        setConfirmOpen(false);
    };

    if (!isOpen) return null;

    return (
        <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ width: '1000px', maxWidth: '95%' }}>
                {/* Consistent Header Design */}
                <div className="staff-modal-header" style={{ background: '#a0adf1' }}>
                    <div className="staff-modal-title" style={{ color: 'white' }}>
                     {t('pageTitles.complaintsList')}
                    </div>
                    <button className="admin-modal-close" onClick={onClose} style={{ color: 'white' }}>
                        <IoClose />
                    </button>
                </div>

                <div className="staff-form-body bg-white p-6">
                    <form onSubmit={handleInternalSubmit}>
                        {/* Boxed Layout Form Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 mb-6">
                            <ModalRow label={t('attendance.attendanceDate')} isRequired>
                                <input type="date" name="complaintDate" value={formData.complaintDate} onChange={handleFieldChange} className="staff-modal-input" required />
                            </ModalRow>

                            <ModalRow label={t('attendance.department')} isRequired>
                                <select name="departmentId" value={formData.departmentId} onChange={handleFieldChange} className="staff-modal-input" required>
                                    <option value="">Select Department</option>
                                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </ModalRow>

                            <ModalRow label={t('complaint.complainerName')} isRequired>
                                <input name="complainerName" value={formData.complainerName} onChange={handleFieldChange} className="staff-modal-input" placeholder="Complainer Name" required />
                            </ModalRow>

                            <ModalRow label={t('complaint.complainAgainst')} isRequired>
                                <input name="complainAgainst" value={formData.complainAgainst} onChange={handleFieldChange} className="staff-modal-input" placeholder="Complaint Against" required />
                            </ModalRow>

                            <div className="col-span-2 flex items-start text-sm">
                                <label className="w-44 font-bold text-gray-600 mt-2">{t('complaint.complaint')} (*)</label>
                                <span className="px-2 mt-2">:</span>
                                <textarea
                                    name="complaintDetails"
                                    className="flex-1 border-2 p-2 rounded-lg bg-gray-50 h-24 outline-none focus:border-blue-400 font-bold text-gray-800"
                                    value={formData.complaintDetails}
                                    onChange={handleFieldChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Attachment Section */}
                        <div className="mb-2">
                            <button type="button" onClick={addRow} className="staff-btn-save text-xs py-1 px-4">
                                {t('common.addAttachment')}
                            </button>
                        </div>

                        <div className="overflow-hidden border rounded-lg mb-6">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-100 text-gray-600">
                                    <tr>
                                        <th className="border p-2 text-center w-1/2">{t('common.description')}</th>
                                        <th className="border p-2 text-center">{t('common.attachments')}</th>
                                        <th className="border p-2 text-center w-20">{t('common.delete')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attachmentRows.map(row => (
                                        <tr key={row.id}>
                                            <td className="border p-1">
                                                <input
                                                    value={row.description}
                                                    onChange={(e) => setAttachmentRows(prev => prev.map(r => r.id === row.id ? { ...r, description: e.target.value } : r))}
                                                    className="staff-modal-input w-full"
                                                    placeholder="Description"
                                                />
                                            </td>
                                            <td className="border p-1">
                                                {row.fileName ? (
                                                    <div className="flex items-center justify-between px-2">
                                                        {/* <span className="text-blue-600  font-bold">
                                                            {row.fileName}
                                                        </span> */}
                                                        {/* {row.fileUrl ? ( */}
                                                        <a
                                                            href={resolveMediaUrl(row.fileUrl)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 underline hover:text-blue-800 break-all"
                                                        >
                                                            {row.fileName || t('common.view')}
                                                        </a>


                                                    </div>
                                                ) : (
                                                    <input type="file" onChange={(e) => handleFileChange(row.id, e)} className="text-[10px] w-full" />
                                                )}
                                            </td>
                                            <td className="border p-1 text-center">
                                                <button type="button" onClick={() => removeRow(row.id)} className="text-red-500 hover:scale-110 transition-transform">
                                                    <IoTrashOutline size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-center border-t pt-6">
                            <button type="submit" className="staff-btn-save px-20 py-2 shadow-lg uppercase tracking-wider">
                                {mode === 'add' ? t('common.save') : t('common.update')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <ConfirmActionModal
                open={confirmOpen}
                onConfirm={handleConfirmFinal}
                onClose={() => setConfirmOpen(false)}
                title={mode === 'add' ? "Confirm Addition" : "Confirm Update"}
                message={`Are you sure you want to ${mode === 'add' ? 'save' : 'update'} these complaint details?`}
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />
        </div>
    );
};

// Helper for the "Label : InputBox" design
const ModalRow = ({ label, children, isRequired }) => (
    <div className="flex items-center text-sm">
        <label className="w-44 font-bold text-gray-600 text-right">{label} {isRequired && "(*)"}</label>
        <span className="px-3 font-bold text-gray-800">:</span>
        <div className="flex-1">
            {children}
        </div>
    </div>
);

export default ComplaintFormModal;  